import { globalSet } from 'app/utils';

export interface IUrl {
    url: string;
    path: string;
}

export interface IUrlObject {
    hash?: string;
    host?: string;
    hostname?: string;
    href?: string;
    origin?: string;
    pathname?: string;
    port?: string;
    protocol?: string;
    search?: string;
}

const SLASH = '/';

export class Url {

    public static getCurrent(): string {
        return window.location.href;
    }

    /**
     * Parse a url so that its components can be accessed individually, from:
     * http://stackoverflow.com/questions/6644654/ but modified so it doesn't
     * retain the HTML anchor elements (no orphaned DOM nodes).
     *
     * @TODO use regex instead of browser specific parsing
     *
     * @param {String} path
     * @returns {IUrlObject}
     */
    public static parse(path: string): IUrlObject {

        const a = document.createElement('a');
        a.href = path;

        const obj: IUrlObject = {
            hash: '',
            host: '',
            hostname: '',
            href: '',
            origin: '',
            pathname: '',
            port: '',
            protocol: '',
            search: ''
        };

        Object.keys(obj).forEach((key) => {
            obj[key] = a[key];
        });

        // IE doesn't doesn't define origin so we are building it ourselves
        if (!obj.port || obj.port === '443' || obj.port === '80') {
            obj.origin = `${obj.protocol}//${obj.hostname}`;
        } else {
            obj.origin = `${obj.protocol}//${obj.hostname}:${obj.port}`;
        }
        // IE11 does not add slash to beginning of pathname
        obj.pathname = this.wrapSlashes(obj.pathname, false, true);

        return obj;
    }

    public static parseSearch(search: string): any {
        const result = {};
        search = search.replace('?', '');
        search.split('&').forEach((str) => {
            const kv = str.split('=');
            const key = kv[0];
            const val = kv[1];
            if (key && val) {
                result[key] = val;
            }
        });
        return result;
    }

    /**
     * Build a url string from an object of url properties.
     *
     * @param {IUrlObject} obj
     * @returns {string}
     */
    public static build(obj: IUrlObject) {

        obj.search = (typeof obj.search === 'object')
            ? Url.param(obj.search)
            : obj.search;

        return [
            obj.origin,
            obj.pathname,
            obj.search,
            obj.hash
        ].join('');
    }

    /**
     * Join all arguments together into a URL string. Removes duplicate slashes
     * (but leaves the double slashes in a protocol, eg: http://foo/bar/baz).
     *
     * @param args
     * @returns {String}
     */
    public static join(...args): string {
        return Url.cleanSlashes(args.join(SLASH));
    }

    /**
     * Serialize an object into a URL-friendly key1=val1&key2=val2 string.
     *
     * @param {Object} obj
     * @returns {String}
     */
    public static param(obj: object): string {
        const pairs = Object.keys(obj).map((key) => {
            const k = encodeURIComponent(key);
            const v = encodeURIComponent(obj[key]);
            return k + '=' + v;
        });
        const str = pairs.join('&');
        return (str) ? '?' + str : '';
    }

    /**
     * Create an absolute URL from a given path.  If the path is already
     * absolute then it is returned as it is. Otherwise the path is made
     * absolute using a base URL or current URL if base is not provided.
     *
     * @param {String} path
     * @param {String} [base]
     * @returns {String}
     */
    public static absolute(path: string, base = Url.getCurrent()): string {
        const isAbsolute = (path.substr(0, 4) === 'http');
        return (isAbsolute) ? path : Url.join(base, path);
    }

    /**
     * Wrap a URL in slashes, ensuring no double slashes. Is careful not
     * to destroy search parameters.
     *
     * Example:
     * Url.wrapSlashes('foo/bar?baz=123'); -> '/foo/bar/?baz=123'
     *
     * @param {String} url
     * @param {Boolean} last
     * @param {Boolean} first
     * @returns {string}
     */
    public static wrapSlashes(url: string, last = true, first = true) {
        const split = url.split('?');
        const search = split[1];
        url = split[0];
        url = (first && (url.substr(0, 4) !== 'http')) ? SLASH + url : url;
        url = (last) ? url + SLASH : url;
        url = (search) ? url + '?' + search : url;
        return Url.cleanSlashes(url);
    }

    /**
     * Remove double slashes from a url except when used in a protocol (https://).
     *
     * @param {String} url
     * @returns {String}
     */
    public static cleanSlashes(url): string {
        return url
            .replace(/([^:]\/)\/+/g, '$1')  // remove from middle
            .replace(/^\/\//, SLASH)        // remove from beginning
            .replace(/\/\/$/, SLASH);       // remove from end
    }

}

globalSet('Utils.Url', Url);



// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./public/app/utils/url/url.ts